import type { AssignableUser, AutomationStep, UpsertAutomation } from '@dnx/contracts';
import { LEAD_SOURCES, PROCEDURE_AREAS } from '@dnx/contracts';
import { Alert, Badge, Button, Card, CardHeader, Field, Input, Select, Spinner, Switch, cn } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowDown, ArrowLeft, ArrowUp, Clock, MessageSquareText, Plus, Shuffle, Tag, Trash2, TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import { assignableQuery } from '../crm/queries.js';
import { api } from '../lib/api.js';
import { AREA_LABELS, SOURCE_LABELS, STAGE_LABELS } from '../lib/labels.js';
import { tagsQuery } from '../reception/queries.js';
import { toBody } from './list-page.js';
import { automationsQuery, EMPTY, MOVE_STAGES, runsQuery, TEMPLATES } from './shared.js';

const NEW_STEP: Record<AutomationStep['type'], () => AutomationStep> = {
  send_message: () => ({ type: 'send_message', text: '' }),
  wait: () => ({ type: 'wait', minutes: 1440 }),
  add_tag: () => ({ type: 'add_tag', tagId: '' }),
  move_stage: () => ({ type: 'move_stage', stage: 'qualifying' }),
  assign: () => ({ type: 'assign', userId: null }),
};
const STEP_META = {
  send_message: { label: 'Enviar mensagem no WhatsApp', Icon: MessageSquareText },
  wait: { label: 'Esperar', Icon: Clock },
  add_tag: { label: 'Aplicar etiqueta', Icon: Tag },
  move_stage: { label: 'Mover para a etapa', Icon: TrendingUp },
  assign: { label: 'Definir responsável', Icon: Shuffle },
} as const;
const UNITS = [['minutos', 1], ['horas', 60], ['dias', 1440]] as const;
const RUN_STATUS = { waiting: ['Em andamento', 'primary'], done: ['Concluída', 'success'], cancelled: ['Interrompida', 'neutral'], failed: ['Erro', 'danger'] } as const;

function Chips<T extends string>({ all, value, onChange, label }: { all: readonly T[]; value: T[] | undefined; onChange: (v: T[]) => void; label: (v: T) => string }) {
  const set = new Set(value ?? []);
  return (
    <div className="flex flex-wrap gap-1.5">
      {all.map((v) => (
        <button key={v} type="button" aria-pressed={set.has(v)} onClick={() => { const n = new Set(set); if (n.has(v)) n.delete(v); else n.add(v); onChange([...n]); }}
          className={cn('h-8 rounded-full border px-3 text-[13px]', set.has(v) ? 'border-primary bg-primary-soft text-primary' : 'border-border-strong text-fg-muted hover:text-fg')}>
          {label(v)}
        </button>
      ))}
    </div>
  );
}

function WaitInput({ minutes, onChange }: { minutes: number; onChange: (m: number) => void }) {
  const unit = minutes % 1440 === 0 ? 1440 : minutes % 60 === 0 ? 60 : 1;
  return (
    <div className="flex gap-2">
      <Input type="number" min={1} className="w-28" value={minutes / unit} aria-label="Quanto tempo"
        onChange={(e) => onChange(Math.max(1, Math.round(Number(e.target.value) || 1)) * unit)} />
      <Select className="w-36" value={unit} aria-label="Unidade" onChange={(e) => onChange(Math.max(1, Math.round(minutes / unit)) * Number(e.target.value))}>
        {UNITS.map(([l, v]) => <option key={v} value={v}>{l}</option>)}
      </Select>
    </div>
  );
}

function StepEditor({ step, onChange, people, tags }: {
  step: AutomationStep; onChange: (s: AutomationStep) => void; people: AssignableUser[]; tags: { id: string; name: string }[];
}) {
  if (step.type === 'send_message') {
    return (
      <div>
        <textarea value={step.text} onChange={(e) => onChange({ ...step, text: e.target.value })} rows={3} maxLength={4096} aria-label="Mensagem"
          placeholder="Oi, {nome}! Aqui é da {clinica}…"
          className="w-full resize-y rounded-md border border-border-strong bg-surface px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:border-primary" />
        <p className="mt-1 flex flex-wrap gap-1.5 text-[12px] text-fg-muted">Variáveis:
          {['{nome}', '{atendente}', '{clinica}'].map((v) => (
            <button key={v} type="button" onClick={() => onChange({ ...step, text: `${step.text}${step.text && !step.text.endsWith(' ') ? ' ' : ''}${v}` })}
              className="rounded border border-border bg-surface-muted px-1.5 font-mono text-[11.5px] text-fg">{v}</button>
          ))}
        </p>
      </div>
    );
  }
  if (step.type === 'wait') return <WaitInput minutes={step.minutes} onChange={(m) => onChange({ ...step, minutes: m })} />;
  if (step.type === 'add_tag') {
    return (
      <Select value={step.tagId} onChange={(e) => onChange({ ...step, tagId: e.target.value })} aria-label="Etiqueta">
        <option value="">Escolha a etiqueta…</option>
        {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </Select>
    );
  }
  if (step.type === 'move_stage') {
    return (
      <Select value={step.stage} onChange={(e) => onChange({ ...step, stage: e.target.value as typeof step.stage })} aria-label="Etapa">
        {MOVE_STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
      </Select>
    );
  }
  return (
    <Select value={step.userId ?? ''} onChange={(e) => onChange({ ...step, userId: e.target.value || null })} aria-label="Responsável">
      <option value="">Próximo do rodízio (distribuição de leads)</option>
      {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
    </Select>
  );
}

/** Editor da automação: gatilho, condições, passos em ordem e histórico das execuções. */
export function AutomationEditorPage({ id, templateIndex }: { id: string; templateIndex?: number }) {
  const isNew = id === 'nova';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: list } = useQuery(automationsQuery);
  const { data: tags = [] } = useQuery(tagsQuery);
  const { data: people = [] } = useQuery(assignableQuery);
  const { data: runs } = useQuery({ ...runsQuery(id), enabled: !isNew });
  const existing = list?.find((a) => a.id === id);
  const [form, setForm] = useState<UpsertAutomation | null>(isNew ? structuredClone(TEMPLATES[templateIndex ?? -1]?.value ?? EMPTY) : null);
  useEffect(() => { if (!isNew && existing && !form) setForm(toBody(existing)); }, [existing, form, isNew]);

  const save = useMutation({
    mutationFn: (f: UpsertAutomation) => api(isNew ? '/automations' : `/automations/${id}`, { method: isNew ? 'POST' : 'PATCH', body: f }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['automations'] }); void navigate({ to: '/automacoes' }); },
  });
  const remove = useMutation({
    mutationFn: () => api<void>(`/automations/${id}`, { method: 'DELETE' }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['automations'] }); void navigate({ to: '/automacoes' }); },
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  if (!form) return <div className="grid h-60 place-items-center"><Spinner className="size-6 text-fg-subtle" label="Carregando" /></div>;

  const set = <K extends keyof UpsertAutomation>(k: K, v: UpsertAutomation[K]) => setForm((f) => ({ ...f!, [k]: v }));
  const setStep = (i: number, s: AutomationStep) => set('steps', form.steps.map((x, j) => (j === i ? s : x)));
  const move = (i: number, d: -1 | 1) => { const s = [...form.steps]; [s[i], s[i + d]] = [s[i + d]!, s[i]!]; set('steps', s); };

  return (
    <div className="mx-auto max-w-4xl">
      <Link to="/automacoes" className="-ml-2 inline-flex h-9 items-center gap-1 rounded-md px-2 text-sm text-fg-muted hover:text-fg"><ArrowLeft className="size-4" />Automações</Link>
      <form className="mt-2 grid gap-5" onSubmit={(e) => { e.preventDefault(); save.mutate(form); }}>
        <Card className="grid gap-5 p-5">
          <Field label="Nome"><Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ex.: Follow-up de lead novo" maxLength={80} /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Quando">
              <Select value={form.trigger} onChange={(e) => { set('trigger', e.target.value as UpsertAutomation['trigger']); set('triggerValue', null); }}>
                <option value="lead_created">Um lead novo chegar</option>
                <option value="lead_stage_changed">O lead mudar para a etapa…</option>
                <option value="tag_added">A etiqueta for aplicada…</option>
              </Select>
            </Field>
            {form.trigger === 'lead_stage_changed' && (
              <Field label="Etapa">
                <Select value={form.triggerValue ?? ''} onChange={(e) => set('triggerValue', e.target.value || null)}>
                  <option value="">Escolha…</option>
                  {Object.entries(STAGE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </Select>
              </Field>
            )}
            {form.trigger === 'tag_added' && (
              <Field label="Etiqueta">
                <Select value={form.triggerValue ?? ''} onChange={(e) => set('triggerValue', e.target.value || null)}>
                  <option value="">Escolha…</option>
                  {tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
              </Field>
            )}
          </div>
          <details className="rounded-lg border border-border p-3.5" open={!!(form.conditions.sources?.length || form.conditions.areas?.length || form.conditions.tagIds?.length)}>
            <summary className="cursor-pointer text-sm font-medium text-fg">Só se… <span className="font-normal text-fg-muted">(opcional)</span></summary>
            <div className="mt-4 grid gap-4">
              <Field label="Origem do lead"><Chips all={LEAD_SOURCES} value={form.conditions.sources} label={(v) => SOURCE_LABELS[v]!} onChange={(v) => set('conditions', { ...form.conditions, sources: v })} /></Field>
              <Field label="Interesse"><Chips all={PROCEDURE_AREAS} value={form.conditions.areas} label={(v) => AREA_LABELS[v]!} onChange={(v) => set('conditions', { ...form.conditions, areas: v })} /></Field>
              {tags.length > 0 && (
                <Field label="Tem as etiquetas"><Chips all={tags.map((t) => t.id)} value={form.conditions.tagIds} label={(v) => tags.find((t) => t.id === v)!.name} onChange={(v) => set('conditions', { ...form.conditions, tagIds: v })} /></Field>
              )}
            </div>
          </details>
        </Card>

        {form.trigger === 'lead_created' && form.steps[0]?.type === 'send_message' && (
          <Alert tone="warning" title="Mensagem imediata para lead novo">
            Se o assistente virtual estiver ligado, ele já responde na hora e o paciente recebe duas saudações. Comece com “Esperar” para um follow-up.
          </Alert>
        )}
        <Card>
          <CardHeader title="Então" description="Os passos rodam em ordem. “Esperar” pausa a sequência (ex.: 1 dia) antes do próximo." />
          <ol className="grid gap-3 p-5">
            {form.steps.map((s, i) => {
              const { label, Icon } = STEP_META[s.type];
              return (
                <li key={i} className="rounded-xl border border-border bg-surface p-4">
                  <div className="mb-3 flex items-center gap-2">
                    <span className="grid size-7 place-items-center rounded-full bg-primary-soft text-[12px] font-semibold text-primary">{i + 1}</span>
                    <span className="flex flex-1 items-center gap-1.5 text-sm font-medium text-fg"><Icon className="size-4 text-fg-subtle" />{label}</span>
                    <Button variant="ghost" size="icon" aria-label="Subir" disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="size-4" /></Button>
                    <Button variant="ghost" size="icon" aria-label="Descer" disabled={i === form.steps.length - 1} onClick={() => move(i, 1)}><ArrowDown className="size-4" /></Button>
                    <Button variant="ghost" size="icon" aria-label="Remover passo" disabled={form.steps.length === 1} onClick={() => set('steps', form.steps.filter((_, j) => j !== i))}><Trash2 className="size-4" /></Button>
                  </div>
                  <StepEditor step={s} onChange={(n) => setStep(i, n)} people={people} tags={tags} />
                </li>
              );
            })}
          </ol>
          <div className="flex flex-wrap gap-2 border-t border-border px-5 py-4">
            {(Object.keys(STEP_META) as AutomationStep['type'][]).map((t) => {
              const { label, Icon } = STEP_META[t];
              return <Button key={t} size="sm" variant="secondary" disabled={form.steps.length >= 20} onClick={() => set('steps', [...form.steps, NEW_STEP[t]()])}><Plus className="size-3.5" /><Icon className="size-3.5" />{label}</Button>;
            })}
          </div>
        </Card>

        <Card className="grid gap-4 p-5">
          <Switch checked={form.stopOnReply} onChange={(v) => set('stopOnReply', v)} label="Parar se o paciente responder"
            description="Recomendado para follow-up: quando a pessoa responde, a equipe assume e as próximas mensagens não saem." />
          <Switch checked={form.enabled} onChange={(v) => set('enabled', v)} label="Automação ligada"
            description="Sempre para se o lead for fechado ou perdido, ou se o paciente responder SAIR." />
        </Card>

        {save.error && <Alert tone="danger">{save.error.message}</Alert>}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            {!isNew && (confirmDelete
              ? <span className="flex items-center gap-2"><Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>Apagar</Button><Button variant="ghost" onClick={() => setConfirmDelete(false)}>Manter</Button><span className="text-[13px] text-fg-muted">O que estiver em andamento para.</span></span>
              : <Button variant="ghost" className="text-danger" onClick={() => setConfirmDelete(true)}><Trash2 className="size-4" />Apagar automação</Button>)}
          </div>
          <div className="flex gap-2">
            <Link to="/automacoes"><Button variant="ghost">Cancelar</Button></Link>
            <Button type="submit" loading={save.isPending}>{isNew ? 'Criar automação' : 'Salvar'}</Button>
          </div>
        </div>
      </form>

      {!isNew && (
        <Card className="mt-8">
          <CardHeader title="Últimas execuções" description="Quem passou por esta automação e onde parou." />
          {!runs?.length ? <p className="px-5 pt-3 pb-5 text-sm text-fg-muted">Nenhuma execução ainda.</p> : (
            <ul className="divide-y divide-border">
              {runs.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 text-sm">
                  <span className="min-w-[160px] flex-1 font-medium text-fg">{r.contactName}</span>
                  <Badge tone={RUN_STATUS[r.status][1]}>{RUN_STATUS[r.status][0]}</Badge>
                  <span className="text-[13px] text-fg-muted">
                    {r.status === 'waiting' ? `próximo passo ${new Date(r.nextAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}`
                      : r.cancelReason ?? r.lastError ?? `passo ${r.stepIndex} de ${form.steps.length}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
